import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { navItems, images } from '../data/mockData';

const ButterflyLogo = ({ className = "h-12 w-auto" }) => {
  const [imgLoaded, setImgLoaded] = useState(true);
  
  if (imgLoaded) {
    return (
      <img
        src={images.butterfly}
        alt="A Healing Touch"
        className={className}
        onError={() => setImgLoaded(false)}
      />
    );
  }
  
  return (
    <svg className={className} viewBox="0 0 80 70" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M40 35 C30 15, 5 5, 10 30 C12 40, 25 42, 40 35" fill="#C9A84C" opacity="0.8"/>
      <path d="M40 35 C50 15, 75 5, 70 30 C68 40, 55 42, 40 35" fill="#C9A84C" opacity="0.8"/>
      <path d="M40 35 C32 45, 15 55, 20 40 C22 36, 30 38, 40 35" fill="#7B4F9E" opacity="0.7"/>
      <path d="M40 35 C48 45, 65 55, 60 40 C58 36, 50 38, 40 35" fill="#7B4F9E" opacity="0.7"/>
      <line x1="40" y1="30" x2="40" y2="60" stroke="#4A154B" strokeWidth="1.5"/>
      <line x1="40" y1="30" x2="35" y2="20" stroke="#4A154B" strokeWidth="1"/>
      <line x1="40" y1="30" x2="45" y2="20" stroke="#4A154B" strokeWidth="1"/>
    </svg>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const allItems = [...navItems.left, ...navItems.right];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? 'bg-[#5B2860]/95 backdrop-blur-lg shadow-xl py-2'
          : 'bg-[#8B5EA5]/80 backdrop-blur-sm py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Navigation */}
        <div className="hidden lg:grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="flex items-center justify-end gap-8">
            {navItems.left.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`hover:text-[#C9A84C] transition-colors duration-300 text-[13px] font-open-sans tracking-[0.15em] uppercase relative group ${
                  location.pathname === item.href ? 'text-[#C9A84C]' : 'text-white/90'
                }`}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#C9A84C] transition-[width] duration-300 group-hover:w-full" />
              </Link>
            ))}
          </div>
          <Link to="/" className="mx-10 transition-transform duration-300 hover:scale-110">
            <ButterflyLogo className="h-12 w-auto drop-shadow-lg" />
          </Link>
          <div className="flex items-center justify-start gap-8">
            {navItems.right.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`hover:text-[#C9A84C] transition-colors duration-300 text-[13px] font-open-sans tracking-[0.15em] uppercase relative group ${
                  location.pathname === item.href ? 'text-[#C9A84C]' : 'text-white/90'
                }`}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#C9A84C] transition-[width] duration-300 group-hover:w-full" />
              </Link>
            ))}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden flex items-center justify-between">
          <Link to="/">
            <ButterflyLogo className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <a href="tel:7196842180" className="text-[#C9A84C] p-2">
              <Phone size={20} />
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white p-2 hover:text-[#C9A84C] transition-colors duration-300"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`lg:hidden fixed inset-0 top-[56px] bg-[#4A154B]/98 backdrop-blur-xl transition-opacity duration-400 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="flex flex-col items-center pt-12 px-6 space-y-1">
          {allItems.map((item, index) => (
            <Link
              key={item.label}
              to={item.href}
              onClick={() => setIsOpen(false)}
              className={`w-full text-center hover:text-[#C9A84C] transition-colors duration-300 text-lg font-open-sans tracking-[0.15em] uppercase py-4 border-b border-white/10 ${
                location.pathname === item.href ? 'text-[#C9A84C]' : 'text-white/90'
              }`}
              style={{
                animationDelay: `${index * 0.05}s`,
              }}
            >
              {item.label}
            </Link>
          ))}
          <div className="pt-8">
            <a
              href="tel:7196842180"
              className="flex items-center gap-2 text-[#C9A84C] text-lg font-semibold"
            >
              <Phone size={20} />
              719.684-2180
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
